import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, usePresence } from 'framer-motion';

import * as cssClassNames from '../../styles/screens/product/IntroV1.module.scss';

import {
    hasTextToDisplay,
    MultiLangText,
    secToMs,
    setAnimationTimeout,
} from '../../shared';
import {
    BaseChapterProps,
} from '../../components/ChapterRenderer';
import CodificationWithTranslations, {
    calculateDuration as calculateCodificationDuration,
    calculateExitDuration as calculateExitCodificationDuration,
    TranslationCodificationProps,
} from '../../components/Codification/CodificationWithTranslations';


const codificationProps: TranslationCodificationProps = {
    characterSwitchAmount: 4,
    characterNextTrigger: 2,
    timingConfig: {
        control: 'character',
        duration: 0.03,
    },
};
const languageTransitionDelay = 0.25;
const pause = 4;


export type IntroV1Props = BaseChapterProps & {
    text?: MultiLangText,
}

export default function IntroV1(props: IntroV1Props) {
    const {
        text,
        languages,
        onDurationsCalculated,
    } = props;

    const hasText = useMemo(() => text && hasTextToDisplay(text, languages), [text, languages]);
    const [langIndex, setLangIndex] = useState(0);
    const [nextLangIndex, setNextLangIndex] = useState(0);
    const [rewrappedText, setRewrappedText] = useState<null | MultiLangText>(hasText ? null : {});
    const [isPresent, safeToRemove] = usePresence();

    useEffect(() => {
        if (nextLangIndex === langIndex) {
            return;
        }

        return setAnimationTimeout(() => {
            setLangIndex(nextLangIndex);
        }, secToMs(pause));
    }, [nextLangIndex]);

    useEffect(() => {
        if (hasText || isPresent) {
            return;
        }

        safeToRemove();
    }, [hasText, isPresent]);

    useEffect(() => {
        if (!onDurationsCalculated || !rewrappedText) {
            return;
        }

        const newData = {
            ...props,
            text: rewrappedText
        };
        const duration = calculateDuration(newData);
        const exitDuration = calculateExitDuration(newData);
        onDurationsCalculated({
            duration,
            exitDuration,
        });
    }, [rewrappedText]);

    const textComponent = useMemo(() => hasText ? (
        <CodificationWithTranslations
            text={text}
            languages={languages}
            langIndex={langIndex}
            codificationProps={codificationProps}
            runFinalTextAnimation
            languageTransitionDelay={languageTransitionDelay}
            onTyped={() => {
                setNextLangIndex(i => {
                    const langs = languages?.length ? languages : ['EN'];
                    return (i >= langs.length - 1) ? i : (i + 1);
                });
            }}
            onTextRewrapped={lines => setRewrappedText(lines)}
        />
    ) : null, [text, langIndex]);

    return (
        <div className={cssClassNames.intro_v1}>
            {hasText ? (
                <div className={cssClassNames.text_container}>
                    <div>
                        <AnimatePresence onExitComplete={isPresent ? undefined : safeToRemove}>
                            {isPresent ? textComponent : null}
                        </AnimatePresence>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function calculateDuration(data: IntroV1Props) {
    const {
        text,
        languages = ['EN'],
    } = data;
    const entryDuration = 0;

    const hasText = text && hasTextToDisplay(text, languages);
    const textDuration = hasText ? calculateCodificationDuration({
        text,
        languages,
        codificationProps,
        languageTransitionDelay,
    }) : 0;

    const mainDuration = textDuration + pause * (hasText ? languages.length : 1);

    const total = entryDuration + mainDuration;

    const duration = secToMs(total) + calculateExitDuration(data);

    return duration;
}

export function calculateExitDuration(data: IntroV1Props) {
    const {
        text,
        languages = ['EN'],
    } = data;

    const hasText = text && hasTextToDisplay(text, languages);
    if (!hasText) {
        return 0;
    }

    const textDuration = calculateExitCodificationDuration({
        text,
        languages,
        codificationProps,
        languageTransitionDelay,
    });

    return secToMs(textDuration);
}
